import { Maybe } from '@/types/globals';
import { Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { CustomMaterial, CustomMaterialEntitiyUniqueId } from '@shared/models/custom-material';
import { CustomSpecification } from '@shared/models/custom-specification';
import { EntitiesByLabIdDto } from '@shared/models/entities-by-lab-id-dto';
import { customMaterialEntityApiToParsedMapper, encodeCustomMaterialId, encodeCustomSpecificationId } from '@shared/utils/material-util';

/**
 * Represents a material dictionary entry.
 *
 * @property customMaterialIds Array of encoded custom material IDs (encodeCustomMaterialId(material)).
 * @property customMaterialIdsByITeroMaterialId Mapping from iTero material ID to encoded custom material ID
 * (material.iTeroMaterialId -> encodeCustomMaterialId(material))
 */
export interface MaterialDictEntry {
	/** Array of encoded custom material IDs (encodeCustomMaterialId(material)). */
	customMaterialIds: string[];
	/** Mapping from iTero material ID to encoded custom material ID. */
	customMaterialIdsByITeroMaterialId: Record<number, string>;
}

/**
 * Type alias for a dictionary mapping either a number or a string (which represents
 * either an encoded custom specification ID or iTero specification ID) to a MaterialDictEntry.
 *
 * If material.customLabSpecificationId exists, use encodeCustomSpecificationId(material.customLabSpecificationId) as the key.
 * Otherwise, use material.iTeroSpecificationId.
 */
export type MaterialDict = Record<number | string, MaterialDictEntry>;

/**
 * Represents the state of materials, specifications, and their dictionaries by lab and indication.
 */
export interface MaterialState {
	/**
	 * Map of encoded custom material IDs to CustomMaterial.
	 * Key is the result of encodeCustomMaterialId(material).
	 */
	materials: Record<string, CustomMaterial>;
	/**
	 * Map of encoded custom specification IDs to CustomSpecification.
	 * Key is the result of encodeCustomSpecificationId(specification).
	 */
	specs: Record<string, CustomSpecification>;
	/**
	 * Dictionary for non-bridge materials.
	 * The first key is the labId (number).
	 * The second key is the bridgeIndicationId (number).
	 * The value is a MaterialDict.
	 */
	nonBridgeDictionary: Record<number, Record<number, MaterialDict>>;
	/**
	 * Dictionary for bridge materials.
	 * The first key is the labId (number).
	 * The second key is the indicationId (number).
	 * The value is a MaterialDict.
	 */
	bridgeDictionary: Record<number, Record<number, MaterialDict>>;
}

export const createInitialState = (): MaterialState => ({
	materials: {},
	specs: {},
	nonBridgeDictionary: {},
	bridgeDictionary: {}
});

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'material' })
export class MaterialStore extends Store<MaterialState> {
	constructor() {
		super(createInitialState());
	}

	updateFromApiEntities(labCustoms: EntitiesByLabIdDto[]): void {
		this.update(MaterialStore.entitiesToState(labCustoms));
	}

	private static entitiesToState(labEntities: EntitiesByLabIdDto[]): MaterialState {
		const materials: Record<string, CustomMaterial> = {};
		const specs: Record<string, CustomSpecification> = {};
		const specsByIdInternal: Record<number, CustomMaterialEntitiyUniqueId> = {};
		const nonBridgeDictionary: MaterialState['nonBridgeDictionary'] = {};
		const bridgeDictionary: MaterialState['bridgeDictionary'] = {};

		labEntities.forEach(labEntity => {
			const labId = labEntity.LabId;

			const customMaterialsFull = labEntity.CustomMaterials || [];
			const customSpecificationsFull = labEntity.CustomSpecifications || [];

			// Keep these unfiltered for a workaround to find material groups for materials that where already deleted or removed.
			customMaterialsFull.forEach(material => {
				const encMatId = encodeCustomMaterialId({ ...material, labId });

				materials[encMatId] = {
					...material,
					...customMaterialEntityApiToParsedMapper(material, labId)
				} satisfies CustomMaterial;
			});

			customSpecificationsFull.forEach(spec => {
				const encSpecId = encodeCustomSpecificationId({ ...spec, labId });

				specs[encSpecId] = {
					...spec,
					...customMaterialEntityApiToParsedMapper(spec, labId)
				} satisfies CustomSpecification;

				specsByIdInternal[spec.id] = { ...spec, labId };
			});

			const nonBridgeByIndication: Record<number, MaterialDict> = {};
			const bridgeByIndication: Record<number, MaterialDict> = {};

			const nonBridgeIndicationIds = new Set<Maybe<number>>();
			const bridgeIndicationIds = new Set<Maybe<number>>();

			const customMaterialsFiltered = customMaterialsFull
				.filter(m => (m?.isActive && !m?.isRemoved) || m?.iTeroMaterialId)
				.map(m => ({ ...m, ...customMaterialEntityApiToParsedMapper(m, labId) }) satisfies CustomMaterial);
			const customSpecificationsFiltered = customSpecificationsFull
				.filter(s => (s?.isActive && !s?.isRemoved) || s?.iTeroSpecificationId)
				.map(s => ({ ...s, ...customMaterialEntityApiToParsedMapper(s, labId) }) satisfies CustomSpecification);

			[...customMaterialsFiltered, ...customSpecificationsFiltered].forEach(customEntity => {
				bridgeIndicationIds.add(customEntity.bridgeIndicationId);
				nonBridgeIndicationIds.add(customEntity.indicationId);
			});

			nonBridgeIndicationIds.delete(undefined);
			nonBridgeIndicationIds.delete(null);
			bridgeIndicationIds.delete(undefined);
			bridgeIndicationIds.delete(null);

			nonBridgeIndicationIds.forEach((indicationId: number) => {
				const filteredMaterials = customMaterialsFiltered.filter(m => m.indicationId === indicationId);
				const filteredSpecs = customSpecificationsFiltered.filter(s => s.indicationId === indicationId);

				nonBridgeByIndication[indicationId] = MaterialStore.getIndicationEntry(filteredMaterials, filteredSpecs, specsByIdInternal);
			});

			bridgeIndicationIds.forEach((bridgeIndicationId: number) => {
				const filteredMaterials = customMaterialsFiltered.filter(m => m.bridgeIndicationId === bridgeIndicationId);
				const filteredSpecs = customSpecificationsFiltered.filter(s => s.bridgeIndicationId === bridgeIndicationId);

				bridgeByIndication[bridgeIndicationId] = MaterialStore.getIndicationEntry(
					filteredMaterials,
					filteredSpecs,
					specsByIdInternal
				);
			});

			if (Object.keys(nonBridgeByIndication).length > 0) {
				nonBridgeDictionary[labId] = nonBridgeByIndication;
			}

			if (Object.keys(bridgeByIndication).length > 0) {
				bridgeDictionary[labId] = bridgeByIndication;
			}
		});

		return {
			materials,
			specs,
			nonBridgeDictionary,
			bridgeDictionary
		};
	}

	private static getIndicationEntry(
		filteredMaterials: CustomMaterial[],
		filteredSpecs: CustomSpecification[],
		specsByIdInternal: Record<number, CustomMaterialEntitiyUniqueId>
	): MaterialDict {
		const materialIdsBySpecId: MaterialDict = filteredMaterials.reduce((acc, mat) => {
			const customSpecificationOrKey = specsByIdInternal?.[mat.customLabSpecificationId as any] ?? {
				id: mat.customLabSpecificationId,
				lastRevisionId: undefined,
				labId: mat.labId
			};
			const specKey = mat.customLabSpecificationId ? encodeCustomSpecificationId(customSpecificationOrKey) : mat.iTeroSpecificationId;

			if (!acc[specKey as any]) {
				acc[specKey as any] = {
					customMaterialIds: [],
					customMaterialIdsByITeroMaterialId: {}
				};
			}

			const dictEntryForSpec = acc[specKey as any];

			dictEntryForSpec.customMaterialIds.push(encodeCustomMaterialId(mat));
			if (mat.iTeroMaterialId) {
				dictEntryForSpec.customMaterialIdsByITeroMaterialId[mat.iTeroMaterialId] = encodeCustomMaterialId(mat);
			}

			return acc;
		}, {} as MaterialDict);

		// Add empty specs
		filteredSpecs.forEach(spec => {
			const specKey = encodeCustomSpecificationId(spec);

			if (!materialIdsBySpecId[specKey]) {
				materialIdsBySpecId[specKey] = {
					customMaterialIds: [],
					customMaterialIdsByITeroMaterialId: {}
				};
			}
		});

		return materialIdsBySpecId;
	}
}
